import { yupResolver } from '@hookform/resolvers/yup'
import Link from 'next/link'
import useTranslation from 'next-translate/useTranslation'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import * as yup from 'yup'

import MainImage from '@/assets/svg/default/contact/main.svg'
import IconTiktok from '@/assets/svg/plain/icon-tiktok--outline.svg'
import IconFacebook from '@/assets/svg/purge/icons/icon-facebook--outline.svg'
import IconInstagram from '@/assets/svg/purge/icons/icon-instagram--outline.svg'
import Breadcrumbs from '@/atoms/Breadcrumb'
import Button from '@/atoms/Button/Button'
import Field from '@/atoms/Field'

import ContactCard from './ContactCard'
import { links } from './data'
import { submitContactForm } from './logic/submitContactForm'
import { ContactForm } from './types'

const CONTACT_PLATFORMS = [
  { href: links.facebook, icon: <IconFacebook key="facebook" /> },
  { href: links.instagram, icon: <IconInstagram key="instagram" /> },
  { href: links.tiktok, icon: <IconTiktok key="tiktok" /> },
]

function Contact() {
  const { t } = useTranslation('contact')

  const form = useForm<ContactForm>({ resolver })

  const errorBody = form.formState.errors.body?.message
  const errorEmail = form.formState.errors.email?.message
  const errorPhone = form.formState.errors.phone?.message
  const errorSubject = form.formState.errors.subject?.message

  const { isLoading, mutate } = useMutation(submitContactForm)

  return (
    <div>
      <Breadcrumbs crumbs={[{ label: t('global:contact') }]} />
      <div className="relative py-12 box">
        <h1 className="relative mb-4 text-3xl font-medium after:h-2 after:bg-[#F6E035] after:w-60 after:block after:rounded-lg after:top-full">
          {t('contact_with_us')}
          <div className="absolute right-0 hidden w-2/3 xl:w-1/3 top-full md:block xl:-top-full lg:hidden xl:block -z-1">
            <MainImage />
          </div>
        </h1>
        <div className="flex flex-col gap-4 justfy-start lg:flex-row">
          <ContactCard
            title={t('customer_service')}
            data={{
              email: 'info@fansi.pl',
            }}
            className="bg-[#4ECDC4] "
          />
          <ContactCard
            title={t('creators_service')}
            data={{
              email: 'start@fansi.pl',
            }}
            className="bg-[#513EA5]"
          />
          <ContactCard title={t('find_us')} className="bg-[#F9698A]">
            {CONTACT_PLATFORMS.map(({ href, icon }) => (
              <a
                key={icon.key}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="inline-block w-8 h-8 mr-3"
              >
                {icon}
              </a>
            ))}
          </ContactCard>
        </div>
        <div className="my-10 text-xl">
          <b>{t('any_questions')} </b>
          <span className="mt-4 text-lg text-gray-100">
            <span>{t('check_out_our_section')}</span>
            <Link href="/help">
              <a className="font-medium text-violet-200">{t('faq')}</a>
            </Link>
            <span>{t('contact_us')}</span>
          </span>
        </div>
        <div className="mt-32">
          <p className="my-4 text-xl font-semibold">Formularz kontaktowy</p>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit((data) => {
                mutate(data), form.reset()
              })}
            >
              <div className="flex flex-col items-center justify-center py-4 space-y-12">
                <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="email" errorMessage={errorEmail}>
                    <Field.Input
                      error={errorEmail}
                      placeholder="mail@example.com"
                      {...form.register('email')}
                    />
                  </Field>
                  <Field label="phone_number" errorMessage={errorPhone}>
                    <Field.Input
                      error={errorPhone}
                      placeholder="555666777"
                      {...form.register('phone')}
                    />
                  </Field>
                  <Field label="message_title" errorMessage={errorSubject}>
                    <Field.Input
                      error={errorSubject}
                      placeholder="Temat wiadomości"
                      {...form.register('subject')}
                    />
                  </Field>
                </div>
                <Field
                  label="message_content"
                  className="w-full"
                  errorMessage={errorBody}
                >
                  <Field.TextArea
                    error={errorBody}
                    placeholder="Treść wiadomości"
                    rows={6}
                    {...form.register('body')}
                  />
                </Field>
                <Button
                  isLoading={isLoading}
                  variant="flat-full"
                  text={t('send')}
                  className="px-6 py-2"
                  type="submit"
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
}

const CONTACT_SCHEMA = yup.object().shape({
  email: yup.string().email('email.invalid').required('field.required'),
  phone: yup
    .string()
    .matches(/^([+][0-9]{2})?[ ]?[0-9]{9}$/, 'phone_number.invalid')
    .required('field.required'),
  subject: yup.string().required('field.required'),
  body: yup.string().required('field.required'),
})

const resolver = yupResolver(CONTACT_SCHEMA)

export default Contact
